import { useCallback, useEffect, useState } from "react";
import { PageHeader } from "../components/layout";
import { Button, Card, StatusIndicator } from "../components/ui";
import { ApiUnavailableState, EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import { formatDate } from "../components/billing";
import { AdvancedJson } from "../components/phase3";
import { useSession } from "../hooks/useSession";
import {
  api,
  apiConfigured,
  ApiError,
  type DuplicateCandidateListView,
  type DuplicateCandidateView,
  type MergeAccountSummary,
  type MergePlanView,
  type MergeResultView,
  type MergeRollbackResultView,
} from "../lib/api";

/**
 * Duplicate accounts — the platform-operator surface (Part 1.4).
 *
 * WHY THIS PAGE EXISTS AT ALL
 *
 * Someone signs in with Discord, comes back a month later, signs in with
 * Google, and now owns two ODIN accounts. Their servers are on one, their
 * subscription may be on the other, and nothing in the product tells them why
 * half their work has vanished. The merge engine fixes that; until this page it
 * was reachable only by hand-writing HTTP requests, which is another way of
 * saying it was not reachable.
 *
 * WHY IT IS NOT A CUSTOMER BUTTON
 *
 * "Merge that account into mine", submitted by a browser, is a request to take
 * over someone else's account unless something outside the request proves both
 * belong to the same person. So: platform operators only, ownership evidence is
 * a provider-VERIFIED email both sides can prove, and an operator who overrides
 * that has their name written into the receipt and the audit log.
 *
 * WHAT THIS PAGE DECIDES, AND WHAT IT ONLY DISPLAYS
 *
 * It decides nothing. Which account survives, whether a pair is safe, what
 * moves and what collapses are all computed server-side and rendered here as
 * given. The operator's inputs are exactly three: which pair, whether to
 * override the canonical choice, and whether to assert ownership without a
 * shared verified email. Everything else on screen is the server's answer.
 *
 * Merging is reversible from its receipt, and the undo is offered on the same
 * screen as the merge rather than filed somewhere an operator would have to
 * already know about.
 */

function isPlatformOperator(platformRole: string): boolean {
  return platformRole === "ADMIN" || platformRole === "OWNER";
}

function toMessage(error: unknown): string {
  if (error instanceof ApiError && error.code === "recent_auth_required") {
    return "For safety, merging needs a sign-in from the last fifteen minutes. Sign out, sign back in, and try again — nothing was changed.";
  }
  if (error instanceof ApiError) return error.message;
  return "Something went wrong. Please try again.";
}

const pairKey = (candidate: { userIdA: string; userIdB: string }): string =>
  `${candidate.userIdA}::${candidate.userIdB}`;

export function AccountMerges(): JSX.Element {
  const { state } = useSession();

  if (!apiConfigured) {
    return (
      <>
        <MergesHeader />
        <NotConfiguredState title="Control API URL not configured">
          Set <code>VITE_CONTROL_API_URL</code> to read duplicate accounts.
        </NotConfiguredState>
      </>
    );
  }

  return (
    <>
      <MergesHeader />
      {state.kind === "loading" ? (
        <LoadingState label="Checking your session…" />
      ) : state.kind !== "authenticated" ? (
        <EmptyState title="Sign in required">This surface requires an authenticated platform operator.</EmptyState>
      ) : !isPlatformOperator(state.me.platformRole) ? (
        <EmptyState title="Platform operator only">
          Merging accounts is a platform-operator action, because merging two accounts on request is
          indistinguishable from taking one over. Your account is not a platform operator.
        </EmptyState>
      ) : (
        <CandidatesLoader />
      )}
    </>
  );
}

function MergesHeader(): JSX.Element {
  return (
    <PageHeader title="Duplicate accounts">
      <p>
        Accounts that appear to belong to the same person — usually because they signed in with Discord once and
        Google another time. Reading this page and previewing a pair change nothing.
      </p>
    </PageHeader>
  );
}

export function CandidatesLoader(): JSX.Element {
  const [list, setList] = useState<DuplicateCandidateListView | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setList(await api.accountMerges.candidates());
    } catch (e) {
      setError(toMessage(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ApiUnavailableState detail={error} />;
  if (!list) return <LoadingState label="Looking for duplicate accounts…" />;
  return <CandidateBoard list={list} onReload={load} />;
}

/** Presentational board. Renders exactly what the server said, and nothing it inferred. */
export function CandidateBoard({
  list,
  onReload,
}: {
  list: DuplicateCandidateListView;
  onReload: () => void | Promise<void>;
}): JSX.Element {
  return (
    <div className="merge-board">
      <div className="dryrun-banner" role="note">
        <strong>Nothing here happens on its own.</strong>
        <span>
          Merging is a deliberate action per pair, it is written to the audit log with your name on it, and it can be
          undone from its receipt.
        </span>
      </div>

      <Card
        title="What the scan found"
        description="Every active account is compared on provider-verified email addresses."
      >
        <ul className="chip-list" aria-label="Duplicate scan summary">
          <li className="count-chip">{list.accountsScanned} accounts scanned</li>
          <li className={`count-chip ${list.safeToMerge > 0 ? "count-chip--ok" : ""}`}>
            {list.safeToMerge} safe to merge
          </li>
          <li className={`count-chip ${list.needingAttention > 0 ? "count-chip--warn" : ""}`}>
            {list.needingAttention} needing attention
          </li>
        </ul>
      </Card>

      {list.candidates.length === 0 ? (
        <EmptyState title="No duplicate accounts found">
          Every account currently holds its own identities. Nothing to merge.
        </EmptyState>
      ) : (
        list.candidates.map((candidate) => (
          <CandidateCard key={pairKey(candidate)} candidate={candidate} onReload={onReload} />
        ))
      )}
    </div>
  );
}

export type Busy = "idle" | "previewing" | "merging" | "rolling-back";

export function CandidateCard({
  candidate,
  onReload,
}: {
  candidate: DuplicateCandidateView;
  onReload: () => void | Promise<void>;
}): JSX.Element {
  const [plan, setPlan] = useState<MergePlanView>(candidate.plan);
  const [assertOwnership, setAssertOwnership] = useState(false);
  const [canonicalOverride, setCanonicalOverride] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState<Busy>("idle");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MergeResultView | null>(null);
  const [rollback, setRollback] = useState<MergeRollbackResultView | null>(null);

  const requestBody = {
    userIdA: candidate.userIdA,
    userIdB: candidate.userIdB,
    ...(assertOwnership ? { operatorAssertsOwnership: true } : {}),
    ...(canonicalOverride ? { canonicalUserId: canonicalOverride } : {}),
  };

  const refreshPlan = async (next: { assert?: boolean; canonical?: string | null }): Promise<void> => {
    const assertNext = next.assert ?? assertOwnership;
    const canonicalNext = next.canonical === undefined ? canonicalOverride : next.canonical;
    setBusy("previewing");
    setError(null);
    try {
      setPlan(
        await api.accountMerges.preview({
          userIdA: candidate.userIdA,
          userIdB: candidate.userIdB,
          ...(assertNext ? { operatorAssertsOwnership: true } : {}),
          ...(canonicalNext ? { canonicalUserId: canonicalNext } : {}),
        }),
      );
      // A changed plan invalidates a confirmation the operator had already
      // started: what they agreed to may no longer be what would happen.
      setConfirming(false);
      setTyped("");
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy("idle");
    }
  };

  const doMerge = async (): Promise<void> => {
    setBusy("merging");
    setError(null);
    try {
      const merged = await api.accountMerges.merge({ ...requestBody, confirmMergedUserId: plan.mergedUserId });
      setResult(merged);
      setConfirming(false);
      setTyped("");
      await onReload();
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy("idle");
    }
  };

  const doRollback = async (): Promise<void> => {
    if (!result) return;
    setBusy("rolling-back");
    setError(null);
    try {
      setRollback(await api.accountMerges.rollback(result.mergedUserId));
      await onReload();
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy("idle");
    }
  };

  if (result) {
    return (
      <MergedCard
        result={result}
        rollback={rollback}
        error={error}
        busy={busy}
        onRollback={() => void doRollback()}
      />
    );
  }

  const closing = plan.merged;
  const surviving = plan.canonical;
  const typedMatches = typed.trim() === closing.username;

  return (
    <Card
      title={`${surviving.username} and ${closing.username}`}
      description={
        candidate.sharedVerifiedEmails.length > 0
          ? `Both accounts have proved the address ${candidate.sharedVerifiedEmails.join(", ")}.`
          : "These accounts do not share a provider-verified email address."
      }
    >
      <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
        {plan.safe ? (
          <StatusIndicator tone="ok" label="Safe to merge" />
        ) : (
          <StatusIndicator tone="warn" label="Needs attention" />
        )}
        {plan.evidence.operatorAsserted ? <span className="count-chip count-chip--warn">operator asserted</span> : null}
      </div>

      <div className="kv-grid" style={{ marginBottom: "0.75rem" }}>
        <AccountColumn heading="Stays" account={surviving} reason={plan.canonicalReason} />
        <AccountColumn heading="Closes" account={closing} reason="Its identities and workspaces move to the account that stays." />
      </div>

      <PlanEffects plan={plan} />

      {plan.blockers.length > 0 ? (
        <div className="ax-confirm" role="note">
          <h4 className="ax-confirm__title">Cannot merge yet</h4>
          <ul className="ax-confirm__list">
            {plan.blockers.map((b) => (
              <li key={b.code}>{b.message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {plan.warnings.length > 0 ? (
        <div className="ax-notice" role="note">
          <strong>Worth knowing first</strong>
          <ul className="ax-confirm__list">
            {plan.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <fieldset style={{ marginTop: "0.75rem", border: "none", padding: 0 }}>
        <legend className="muted">Operator overrides</legend>
        <label className="row" style={{ gap: "0.5rem", alignItems: "flex-start" }}>
          <input
            type="checkbox"
            checked={canonicalOverride === closing.userId}
            disabled={busy !== "idle"}
            onChange={(e) => {
              const next = e.target.checked ? closing.userId : null;
              setCanonicalOverride(next);
              void refreshPlan({ canonical: next });
            }}
          />
          <span>
            Keep <strong>{closing.username}</strong> instead, and close {surviving.username}.
          </span>
        </label>
        <label className="row" style={{ gap: "0.5rem", alignItems: "flex-start", marginTop: "0.5rem" }}>
          <input
            type="checkbox"
            checked={assertOwnership}
            disabled={busy !== "idle"}
            onChange={(e) => {
              setAssertOwnership(e.target.checked);
              void refreshPlan({ assert: e.target.checked });
            }}
          />
          <span>
            I have confirmed outside ODIN that these are the same person. Recorded in the audit log and in the merge
            receipt under your name.
          </span>
        </label>
      </fieldset>

      {error ? (
        <p className="ax-confirm__error" role="alert">
          {error}
        </p>
      ) : null}

      {confirming ? (
        <div className="ax-confirm" role="group" aria-label="Confirm merge">
          <h4 className="ax-confirm__title">
            Merging {closing.username} into {surviving.username}
          </h4>
          <ul className="ax-confirm__list">
            <li>{closing.username} stops being an account. It cannot sign in again.</li>
            <li>
              Its sign-in methods and workspaces move to {surviving.username}, which then owns everything both accounts
              had.
            </li>
            <li>Both accounts are signed out everywhere.</li>
            <li>This can be undone from the receipt, except the sign-outs.</li>
          </ul>
          <label className="field">
            <span className="field__label">Type {closing.username} to confirm</span>
            <input
              className="field__input"
              value={typed}
              autoComplete="off"
              disabled={busy !== "idle"}
              onChange={(e) => setTyped(e.target.value)}
              aria-label={`Type ${closing.username} to confirm`}
            />
          </label>
          <div className="ax-rail-actions">
            <Button
              variant="primary"
              disabled={!typedMatches || busy !== "idle"}
              onClick={() => void doMerge()}
            >
              {busy === "merging" ? "Merging…" : "Merge accounts"}
            </Button>
            <Button
              variant="ghost"
              disabled={busy === "merging"}
              onClick={() => {
                setConfirming(false);
                setTyped("");
              }}
            >
              Keep both accounts
            </Button>
          </div>
        </div>
      ) : (
        <div className="row" style={{ gap: "0.5rem", marginTop: "0.75rem" }}>
          <Button
            variant="primary"
            disabled={!plan.safe || busy !== "idle"}
            title={plan.safe ? undefined : "Resolve the blockers above first."}
            onClick={() => setConfirming(true)}
          >
            Merge accounts
          </Button>
          <Button variant="secondary" disabled={busy !== "idle"} onClick={() => void refreshPlan({})}>
            {busy === "previewing" ? "Checking…" : "Check again"}
          </Button>
        </div>
      )}

      <AdvancedJson title="Advanced — the plan as the server computed it" value={plan} />
    </Card>
  );
}

function AccountColumn({
  heading,
  account,
  reason,
}: {
  heading: string;
  account: MergeAccountSummary;
  reason: string;
}): JSX.Element {
  return (
    <div className="kv">
      <span className="kv__k">{heading}</span>
      <span className="kv__v">
        <strong>{account.username}</strong>
        <br />
        {account.primaryEmail ?? "no email on file"}
        <br />
        <span className="muted">
          created {formatDate(account.createdAt)} · {account.accountStatus.toLowerCase()}
        </span>
        <br />
        <span className="muted">{reason}</span>
        <ul className="chip-list" style={{ marginTop: "0.35rem" }}>
          {account.identities.map((i) => (
            <li key={`${i.provider}-${i.providerEmail ?? "none"}`} className="count-chip">
              {i.provider}
              {i.providerEmailVerified ? " · verified" : " · unverified"}
            </li>
          ))}
          {account.workspaces.map((w) => (
            <li key={w.tenantId} className={`count-chip ${w.hasPaidSubscription ? "count-chip--ok" : ""}`}>
              {w.tenantName} · {w.role.toLowerCase()}
              {w.hasPaidSubscription ? " · paying" : ""}
            </li>
          ))}
        </ul>
      </span>
    </div>
  );
}

function PlanEffects({ plan }: { plan: MergePlanView }): JSX.Element {
  const nothingMoves =
    plan.workspacesMoving.length === 0 &&
    plan.workspacesCollapsing.length === 0 &&
    plan.signInMethodsMoving.length === 0;

  if (nothingMoves) {
    return <p className="muted">Nothing would move: the closing account holds no sign-in methods or workspaces.</p>;
  }

  return (
    <ul className="chip-list" aria-label="What merging would change">
      {plan.signInMethodsMoving.map((m) => (
        <li key={`${m.provider}-${m.providerEmail ?? "none"}`} className="count-chip">
          {m.provider} sign-in moves{m.providerEmail ? ` (${m.providerEmail})` : ""}
        </li>
      ))}
      {plan.workspacesMoving.map((w) => (
        <li key={w.tenantId} className="count-chip">
          {w.tenantName} moves as {w.role.toLowerCase()}
        </li>
      ))}
      {plan.workspacesCollapsing.map((w) => (
        <li key={w.tenantId} className="count-chip count-chip--warn">
          {w.tenantName}: two memberships become one {w.resultingRole.toLowerCase()}
        </li>
      ))}
      {plan.clearsLegacyDiscordUserId ? <li className="count-chip">legacy Discord id cleared</li> : null}
    </ul>
  );
}

export function MergedCard({
  result,
  rollback,
  error,
  busy,
  onRollback,
}: {
  result: MergeResultView;
  rollback: MergeRollbackResultView | null;
  error: string | null;
  busy: Busy;
  onRollback: () => void;
}): JSX.Element {
  return (
    <Card
      title={rollback ? "Merge undone" : "Accounts merged"}
      description={
        rollback
          ? "The accounts are separate again. Both people need to sign in once more — sign-outs cannot be undone."
          : "One account now holds both sets of sign-in methods and workspaces."
      }
    >
      <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
        <StatusIndicator tone={rollback ? "neutral" : "ok"} label={rollback ? "Reversed" : "Merged"} />
      </div>

      <ul className="chip-list" aria-label="What the merge changed">
        <li className="count-chip">{result.signInMethodsMoved} sign-in methods moved</li>
        <li className="count-chip">{result.workspacesMoved} workspaces moved</li>
        <li className="count-chip">{result.workspacesCollapsed} memberships collapsed</li>
        <li className="count-chip">{result.rolesRaised} roles raised</li>
        <li className="count-chip">{result.sessionsRevoked} sessions signed out</li>
      </ul>

      {result.warnings.length > 0 ? (
        <div className="ax-notice" role="note">
          <strong>Worth knowing</strong>
          <ul className="ax-confirm__list">
            {result.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {error ? (
        <p className="ax-confirm__error" role="alert">
          {error}
        </p>
      ) : null}

      {rollback ? (
        <ul className="chip-list" aria-label="What the rollback restored" style={{ marginTop: "0.75rem" }}>
          <li className="count-chip">{rollback.identitiesRestored} sign-in methods returned</li>
          <li className="count-chip">{rollback.membershipsRestored} workspaces returned</li>
          <li className="count-chip">{rollback.membershipsRecreated} memberships recreated</li>
          <li className="count-chip count-chip--warn">{rollback.sessionsNotRestored} sign-outs not undone</li>
        </ul>
      ) : (
        <div className="row" style={{ gap: "0.5rem", marginTop: "0.75rem" }}>
          <Button variant="secondary" disabled={busy !== "idle"} onClick={onRollback}>
            {busy === "rolling-back" ? "Undoing…" : "Undo this merge"}
          </Button>
        </div>
      )}

      <AdvancedJson title="Advanced — the merge receipt" value={rollback ?? result.receipt} />
    </Card>
  );
}
